/**
 * SearchPop module
 * This module exports the SearchPop component, which is a modal for searching and selecting datasets.
 * It also includes the DatasetDetails component and a custom hook to manage the selected dataset.
 *
 * Dependencies:
 * - react, react-router-dom, antd, underscore, and store
 */

import React, { FC, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal, Select } from 'antd';
import { isEmpty } from 'underscore';
import { StoreContext } from '../../../../store/store';
import { IDatasetBrief } from '../../../../typings/metadata';
import styles from '../../Style/SearchPop.module.scss';
import SearchMetadata from './SearchMetadata';

const { Option } = Select;

interface PrivateProps {
  visible: boolean;
  onHandleDatasetsModalCancel: () => void;
  searchID: (id: string) => void;
}

interface DatasetDetailsProps {
  dataset: IDatasetBrief;
}

/**
 * DatasetDetails: A component to display the details of a selected dataset
 *
 * @param {Object} props - The properties passed to this component
 * @returns {ReactElement} The rendered DatasetDetails component
 */
const DatasetDetails: FC<DatasetDetailsProps> = ({ dataset }) => (
  <div className={styles.datasetContainer}>
    <p>
      <b className={styles.datasetLabel}>Title:</b>
      <span>{dataset.Title}</span>
    </p>
    <p>
      <b className={styles.datasetLabel}>Acronym:</b>
      <span>{dataset.Acronym}</span>
    </p>
    <p>
      <b className={styles.datasetLabel}>IsLoginRequired:</b>
      <span>{dataset.IsLoginRequired}</span>
    </p>
    <p>
      <b className={styles.datasetLabel}>Description:</b>
      <span>{dataset.Description}</span>
    </p>
    <div>
      <SearchMetadata options={dataset.Id}></SearchMetadata>
    </div>
  </div>
);

/**
 * Custom hook to manage selected dataset and handle interactions
 * @param {(id: string) => void} searchID - Function to handle searching by dataset ID
 * @param {() => void} onHandleDatasetsModalCancel - Function to handle the closing of the modal
 * @returns {Object} An object containing state values and functions related to the selected dataset
 */
function useSelectedDataset(
  searchID: (id: string) => void,
  onHandleDatasetsModalCancel: () => void
) {
  const { datasetBriefs, searchId } = useContext(StoreContext);
  const [selectDataset, setSelectDataset] = useState<IDatasetBrief | null>(
    null
  );
  const [selectedSearchId, setSelectedSearchId] = useState<string>('');

  const history = useHistory();

  useEffect(() => {
    if (!isEmpty(searchId)) {
      onChange(searchId);
    }
  }, [searchId, datasetBriefs]);

  /**
   * onChange: Handles the onChange event for the Select component and sets the selected dataset
   *
   * @param {string} id - The selected dataset ID
   */
  function onChange(id: string) {
    const selectItem = datasetBriefs.find((d) => d.Id === id);
    if (!selectItem) {
      setSelectDataset(null);
      setSelectedSearchId('');
      return;
    }
    searchID(id);
    setSelectDataset({ ...selectItem });
    setSelectedSearchId(id);
    history.push(`/metadata/${selectItem.Id}`);
  }

  function handleCancel() {
    searchID('');
    setSelectDataset(null);
    setSelectedSearchId('');
    onHandleDatasetsModalCancel();
  }

  return {
    selectDataset,
    onChange,
    handleCancel,
    datasetBriefs,
    searchId,
    selectedSearchId,
  };
}

/**
 * SearchPop: A modal component to search and select datasets
 *
 * @param {Object} props - The properties passed to this component
 * @returns {ReactElement} The rendered SearchPop component
 */
const SearchPop: FC<PrivateProps> = ({
  visible,
  onHandleDatasetsModalCancel,
  searchID,
}) => {
  const {
    selectDataset,
    onChange,
    handleCancel,
    datasetBriefs,
    searchId,
    selectedSearchId,
  } = useSelectedDataset(searchID, onHandleDatasetsModalCancel);

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    if (visible && isEmpty(searchId)) {
      // Delay the opening of the dropdown by 300ms to allow for the modal animation to finish
      const timeout = setTimeout(() => {
        setDropdownOpen(true);
      }, 300);

      // Clear the timeout when the component unmounts or when `visible` changes
      return () => clearTimeout(timeout);
    } else {
      setDropdownOpen(false);
    }
  }, [visible, searchId]);

  return (
    <Modal
      title="Search datasets"
      visible={visible}
      onOk={() => {}}
      onCancel={handleCancel}
      footer={null}
      width={1000}
    >
      <Select
        open={dropdownOpen}
        onDropdownVisibleChange={setDropdownOpen}
        showSearch
        value={selectedSearchId}
        style={{ width: '100%' }}
        placeholder="Select a dataset"
        optionFilterProp="children"
        onChange={onChange}
        filterOption={(input, option: any) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {datasetBriefs.map((data, index) => {
          return (
            <Option key={data.Id} value={data.Id}>
              {`${index + 1},  ${data.ShortDescription}`}
            </Option>
          );
        })}
      </Select>

      {selectDataset && <DatasetDetails dataset={selectDataset} />}
    </Modal>
  );
};

export default SearchPop;
